/**
 * Bookd - Profile Completion Banner
 * Shows Bookees what they need to complete to start getting bookings
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  AlertCircle, Camera, FileText, MapPin, DollarSign, 
  User, CheckCircle, ArrowRight, X 
} from 'lucide-react';

const ProfileCompletionBanner = ({ onDismiss, showDismiss = true }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check if user is a bookee/talent
  const isBookee = user?.roles?.includes('bookee') || user?.user_type === 'talent';
  
  if (!isBookee || !user) return null;
  
  // Calculate profile completion
  const completionChecks = [
    { 
      key: 'photo', 
      label: 'Profile Photo', 
      completed: !!user.picture,
      icon: Camera,
      required: true
    },
    { 
      key: 'bio', 
      label: 'Bio', 
      completed: !!user.bio && user.bio.length > 20,
      icon: FileText,
      required: true
    },
    { 
      key: 'location', 
      label: 'Location', 
      completed: !!user.location,
      icon: MapPin,
      required: true
    },
    { 
      key: 'portfolio', 
      label: 'Portfolio (1+ images)', 
      completed: user.portfolio_images?.length > 0,
      icon: Camera,
      required: true
    },
    { 
      key: 'rate', 
      label: 'Hourly Rate', 
      completed: !!user.hourly_rate && user.hourly_rate > 0,
      icon: DollarSign,
      required: true
    },
    { 
      key: 'category', 
      label: 'Service Category', 
      completed: user.categories?.length > 0,
      icon: User,
      required: true
    },
  ];
  
  const requiredChecks = completionChecks.filter(c => c.required);
  const completedCount = requiredChecks.filter(c => c.completed).length;
  const totalRequired = requiredChecks.length;
  const completionPercent = Math.round((completedCount / totalRequired) * 100);
  const isComplete = completedCount === totalRequired;
  
  // Don't show if profile is complete
  if (isComplete) return null;
  
  const incompleteItems = completionChecks.filter(c => !c.completed);

  return (
    <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 rounded-lg bg-amber-500/20 shrink-0">
            <AlertCircle className="h-5 w-5 text-amber-500" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-amber-600 mb-1">
              Complete Your Profile to Get Bookd
            </h3>
            <p className="text-sm text-muted-foreground mb-3">
              Bookers are more likely to hire talent with complete profiles. You're {completionPercent}% there!
            </p>
            
            {/* Progress Bar */}
            <div className="mb-3">
              <Progress value={completionPercent} className="h-2" />
            </div>
            
            {/* Missing Items */}
            <div className="flex flex-wrap gap-2 mb-3">
              {incompleteItems.slice(0, 3).map((item) => (
                <div 
                  key={item.key}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-muted/50 text-xs"
                >
                  <item.icon className="h-3 w-3 text-muted-foreground" />
                  <span>{item.label}</span>
                </div>
              ))}
              {incompleteItems.length > 3 && (
                <span className="text-xs text-muted-foreground px-2 py-1">
                  +{incompleteItems.length - 3} more
                </span>
              )}
            </div>
            
            <Button
              size="sm"
              onClick={() => navigate('/settings')}
              className="rounded-lg"
            >
              Complete Profile
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
        
        {showDismiss && (
          <button
            onClick={onDismiss}
            className="p-1 rounded-lg hover:bg-muted/50 transition-colors shrink-0"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
      </div>
    </div>
  );
};

// Hook to check if profile is complete (for blocking actions)
export const useProfileCompletion = () => {
  const { user } = useAuth();
  
  const isBookee = user?.roles?.includes('bookee') || user?.user_type === 'talent';
  
  if (!isBookee || !user) {
    return { isComplete: true, completionPercent: 100, missingItems: [] };
  }
  
  const checks = [
    { key: 'photo', completed: !!user.picture },
    { key: 'bio', completed: !!user.bio && user.bio.length > 20 },
    { key: 'location', completed: !!user.location },
    { key: 'portfolio', completed: user.portfolio_images?.length > 0 },
    { key: 'rate', completed: !!user.hourly_rate && user.hourly_rate > 0 },
    { key: 'category', completed: user.categories?.length > 0 },
  ];
  
  const completedCount = checks.filter(c => c.completed).length;
  const completionPercent = Math.round((completedCount / checks.length) * 100);
  const missingItems = checks.filter(c => !c.completed).map(c => c.key);
  
  return {
    isComplete: completedCount === checks.length,
    completionPercent,
    missingItems,
    checkStatus: checks
  };
};

export default ProfileCompletionBanner;
